import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { FlattenedCustomerVariantPreferencesSchema } from "../agents/returns-agents/exchange-agent/customer-variant-preferences";
import { ShippingRateTableSchema } from "../integration/redo-in-shipping/shipping-rate-table";
import { PriceSetSchema } from "../money/price-set";
import { Provider } from "../order";
import {
  AdjustmentZodSchema,
  Channel,
  PickupLocation,
  ProvisionType,
  ReturnTypeEnum,
  ReviewingParty,
  UpsellBillingTrigger,
} from "../return";
import { InputAnswerPartWithStepSchema, Payer } from "../return-flow";
import { ShippingProviderType } from "../shipment";
import { ThirdPartyRefundProvider } from "../third-party-refunds/provider";
import { RepairFeeCollectionStrategy } from "../warranties";
import {
  AdvancedExchangeItemSchema,
  NewExchangeItemSchema,
  PendingReturnItemSchema,
  ReturnableItemSchema,
} from "./draft-return-items";

export enum DraftReturnSource {
  FROM_ORDER = "from_order",
  FROM_WARRANTY_REGISTRATION = "from_warranty_registration",
  FROM_DRAFT_REGISTRATION = "from_draft_registration",
  FROM_PRODUCTS = "from_products",
}

// The order is important! This is the order we will display the compensation methods in the UI
export enum CompensationMethodType {
  EXCHANGE = "exchange",
  STORE_CREDIT = "storeCredit",
  REFUND = "refund",
  REPAIR = "repair",
}

export const COMPENSATION_TYPES = Object.values(CompensationMethodType);

export const CompensationMethodSchema = z.object({
  type: z.nativeEnum(CompensationMethodType),
  // keys are pendingItemIds
  itemValues: z.record(
    z.string(),
    z.object({
      strategy: z.enum(["refund", "storeCredit", "repair"]),
      value: zExt.big(),
      exchangeCredit: zExt.big(),
      isVariantExchange: z.boolean(),
      tax: zExt.big(),
      chargeRepairFee: z.boolean().nullish(),
    }),
  ),
  totalReturnableValue: zExt.big(),
  // keys are pendingItemIds
  /** @deprecated use returnLevelAdjustments and itemLevelAdjustments instead */
  adjustments: z.record(z.string(), zExt.big()),
  returnLevelAdjustments: z.array(AdjustmentZodSchema).nullish(),
  itemLevelAdjustments: z
    .record(z.string(), z.array(AdjustmentZodSchema))
    .nullish(),
  shippingRequired: z.record(z.string(), z.boolean()),
  manualReview: z.boolean(),
  manualReviewReason: z.string().nullish(),
  flagged: z.boolean(),
  flaggedReason: z.string().nullish(),
  payer: z.nativeEnum(Payer),
  returnMethodRequired: z.boolean(),
  exchangeShippingName: z.string().nullish(),
  chargeNewOrderShipping: z.boolean(),
  discountCodeName: z
    .object({ prefix: z.string().nullish(), suffix: z.string().nullish() })
    .nullish(),
  advancedExchangeAvailable: z.boolean(),
  itemsToRefundPoints: z.array(z.string()).nullish(),
  itemsToDeductPoints: z.array(z.string()).nullish(),
  tags: z.array(z.object({ name: z.string(), color: z.string() })).nullish(),
});

export type CompensationMethod = z.infer<typeof CompensationMethodSchema>;
export type CompensationType = CompensationMethod["type"];

export const InteractiveMapLocationSchema = z.object({
  name: z.string(),
  address: z.object({
    address1: z.string(),
    address2: z.string().nullish(),
    city: z.string(),
    state: z.string().nullish(),
    zip: z.string(),
    country: z.string(),
  }),
  geo: z.object({ latitude: z.number(), longitude: z.number() }).nullish(),
  distance: z.string().nullish(),
  hours: z.record(z.string(), z.string()).nullish(),
});

export type InteractiveMapLocation = z.infer<
  typeof InteractiveMapLocationSchema
>;

// Enum definitions
export const ExchangeTypeContext = z.enum(["VARIANT", "ADVANCED"]);
export const PickupLocationEnum = z.nativeEnum(PickupLocation);
export const AdjustmentType = z.enum(["percentage", "amount"]);
export const ReturnOrClaimType = z.enum(["return", "claim"]);
export const ReturnRequestStatus = z.enum(["PENDING", "REJECTED"]);

// Schema definitions
const AddressSchema_ = z.object({
  name: z.string(),
  email: z.string(),
  street1: z.string(),
  street2: z.string().nullish(),
  state: z.string().nullish(),
  city: z.string(),
  country: z.string(),
  zip: z.string(),
  phone: z.string().nullish(),
  returnerName: z.string().nullish(),
  returnerEmail: z.string().nullish(),
  _id: z.string().nullish(),
});
export const AddressSchema: z.ZodType<Address> = AddressSchema_;
export interface Address extends z.infer<typeof AddressSchema_> {}

const PickupDataSchema_ = z.object({
  email: z.string().nullish(),
  phone: z.string().nullish(),
  pickupDate: z.string(),
  pickupLocation: z.object({
    packageLocation: PickupLocationEnum,
    specialInstructions: z.string().nullish(),
  }),
  textReminder: z.boolean(),
});
const PickupDataSchema: z.ZodType<PickupData> = PickupDataSchema_;
export interface PickupData extends z.infer<typeof PickupDataSchema_> {}

export enum ShipmentMethodName {
  PICKUP = "PICKUP",
  IN_STORE = "IN_STORE",
  SETTLEMENT = "SETTLEMENT",
  SELF_SHIP = "SELF_SHIP",
  HAPPY_RETURNS = "HAPPY_RETURNS",
  BLUE_YONDER = "BLUE_YONDER",
  RETURN_BEAR = "RETURN_BEAR",
  MANUAL_LABEL = "MANUAL_LABEL", //Merchant will create the label and send it to the customer
}

export const ShipmentMethodSchema = z.union([
  z.object({
    method: z.literal(ShipmentMethodName.PICKUP),
    pickupData: PickupDataSchema,
  }),
  z.object({ method: z.literal(ShipmentMethodName.IN_STORE) }),
  z.object({ method: z.literal(ShipmentMethodName.SETTLEMENT) }),
  z.object({ method: z.literal(ShipmentMethodName.HAPPY_RETURNS) }),
  z.object({ method: z.literal(ShipmentMethodName.BLUE_YONDER) }),
  z.object({ method: z.literal(ShipmentMethodName.RETURN_BEAR) }),
  z.object({ method: z.literal(ShipmentMethodName.SELF_SHIP) }),
  z.object({ method: z.literal(ShipmentMethodName.MANUAL_LABEL) }),
]);
export type ShipmentMethod = z.infer<typeof ShipmentMethodSchema>;

const SettlementSchema_ = z.object({
  accepted: z.boolean(),
  refund: z.number(),
});
export const SettlementSchema: z.ZodType<Settlement> = SettlementSchema_;
export interface Settlement extends z.infer<typeof SettlementSchema_> {}

const discountNameSchema_ = z.object({ title: z.string(), code: z.string() });
export const discountNameSchema: z.ZodType<DiscountName> = discountNameSchema_;
export interface DiscountName extends z.infer<typeof discountNameSchema_> {}

const ShipmentAddressSchema_ = z.object({
  name: z.string().nullish(),
  street1: z.string().nullish(),
  street2: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  country_name: z.string().nullish(),
  phone: z.string().nullish(),
  id: z.string().nullish(),
  externalLocationId: z.string().nullish(),
  trackstarWarehouseId: z.string().nullish(),
  inmarLocationId: z.string().nullish(),
  blueYonderLocationId: z.string().nullish(),
  aims360WarehouseId: z.string().nullish(),
  isSekoHub: z.boolean().nullish(),
  _id: zExt.objectId().nullish(),
});
export const ShipmentAddressSchema: z.ZodType<ShipmentAddress> =
  ShipmentAddressSchema_;
export interface ShipmentAddress
  extends z.infer<typeof ShipmentAddressSchema_> {}

const HappyReturnsLocationSchema_ = z.object({
  name: z.string(),
  address: z.object({
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zipcode: z.string(),
  }),
  distance: z.number(),
  lat: z.number(),
  lng: z.number(),
});
export const HappyReturnsLocationSchema: z.ZodType<HappyReturnsLocation> =
  HappyReturnsLocationSchema_;
export interface HappyReturnsLocation
  extends z.infer<typeof HappyReturnsLocationSchema_> {}

const HappyReturnsDetailsSchema_ = z.object({
  price: zExt.big(),
  locations: z.array(HappyReturnsLocationSchema),
  zipCode: z.string(),
  retailerId: z.string(),
});
export const HappyReturnsDetailsSchema: z.ZodType<HappyReturnsDetails> =
  HappyReturnsDetailsSchema_;
export interface HappyReturnsDetails
  extends z.infer<typeof HappyReturnsDetailsSchema_> {}

const BlueYonderLocationSchema_ = z.object({
  blueyonderLocationId: z.string(),
  locationName: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string(), // e.g., "1.2 miles"
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const BlueYonderLocationSchema: z.ZodType<BlueYonderLocation> =
  BlueYonderLocationSchema_;
export interface BlueYonderLocation
  extends z.infer<typeof BlueYonderLocationSchema_> {}

const BlueYonderDetailsSchema_ = z.object({
  locations: z.array(BlueYonderLocationSchema),
  retailerCompanyId: z.string(),
});
export const BlueYonderDetailsSchema: z.ZodType<BlueYonderDetails> =
  BlueYonderDetailsSchema_;
export interface BlueYonderDetails
  extends z.infer<typeof BlueYonderDetailsSchema_> {}

const DropoffLocationSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  address: z.object({
    street1: z.string(),
    street2: z.string().optional(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
    country: z.string(),
  }),
  distance: z.string().describe("e.g., '1.2 miles', MUST BE IN MILES"), // e.g., "1.2 miles"
  geo: z.object({ latitude: z.number(), longitude: z.number() }),
});

export const DropoffLocationSchema: z.ZodType<DropoffLocation> =
  DropoffLocationSchema_;
export interface DropoffLocation
  extends z.infer<typeof DropoffLocationSchema_> {}

const ReturnBearDetailsSchema_ = z.object({
  returnBearCaseId: z.string(),
  returnBearCaseShortId: z.string(),
  returnBearCaseVersion: z.number(),
  locations: z.array(DropoffLocationSchema),
  shippingLabelAvailability: z.boolean(),
  flatRateFee: z.number(),
});

export const ReturnBearDetailsSchema: z.ZodType<ReturnBearDetails> =
  ReturnBearDetailsSchema_;
export interface ReturnBearDetails
  extends z.infer<typeof ReturnBearDetailsSchema_> {}

const DraftReturnShipmentSchema_ = z.object({
  carrier: z.string(),
  fee: z.string(),
  flatRateFee: z.string().nullish(),
  pickupFee: z.string().nullish(),
  pickupIneligibility: z.string().nullish(),
  address: ShipmentAddressSchema,
  pendingReturnItems: z.array(z.string()),
  happyReturnsDetails: HappyReturnsDetailsSchema.nullish(),
  blueYonderDetails: BlueYonderDetailsSchema.nullish(),
  returnBearDetails: ReturnBearDetailsSchema.nullish(),
  inStoreLocations: z.array(InteractiveMapLocationSchema).nullish(),
  shippingProviderRate: z.string().nullish(),
  merchantUpcharge: z.string().nullish(),
  customerUpchargeFee: z.number().nullish(),
  marketRate: z.string().nullish(),
  rateSource: z
    .object({
      isMerchantCarrier: z.boolean().nullish(),
      shippingProviderType: z.nativeEnum(ShippingProviderType).nullish(),
      isFromAlternativeAccount: z.boolean().nullish(),
    })
    .nullish(),
});
export const DraftReturnShipmentSchema: z.ZodType<DraftReturnShipment> =
  DraftReturnShipmentSchema_;
export interface DraftReturnShipment
  extends z.infer<typeof DraftReturnShipmentSchema_> {}

const PriceRangeSchema = z.tuple([z.number(), z.number()]);
export type PriceRange = z.infer<typeof PriceRangeSchema>;

const ReturnTotalCalculationsSchema_ = z.object({
  pickupAccept: z.boolean(),
  pickupFee: z.number().nullish(),
  inStoreAccept: z.boolean(),
  settlementAccept: z.boolean(),
  settlementRefund: z.number(),
  shippingFee: z.number(),
  customerUpchargeFee: z.number().optional(),
  exchangeShippingFee: z.number(),
  totalShippingFee: z.number(),
  labelDeducted: z.boolean(),
  canDeductLabel: z.boolean().nullish(),
  returnValue: z.number(),
  totalTaxes: z.number(),
  newOrderValue: z.number(),
  totalNewOrderTaxes: z.number(),
  totalAdjustmentValue: z.number(),
  totalReturnValue: z.number(),
  totalStoreCredit: z.number(),
  totalRefund: z.number(),
  totalRepairFee: PriceRangeSchema,
  hasGreenReturnItems: z.boolean(),
  hasNonGreenReturnItems: z.boolean(),
  showNewItems: z.boolean(),
  showNewOrderAddress: z.boolean(),
  paymentOwed: z.number(),
  totalPaymentOwed: z.number(),
  paymentOwedRange: PriceRangeSchema.nullish(),
  skipPayment: z.boolean(),
  totalLoyaltyPointsDeducted: z.number(),
  totalLoyaltyPointsRefunded: z.number(),
  totalReturnValueDeductedDueToLoyaltyPoints: z.number(),
  totalOrderShipping: z.number(),
  newOrderDiscount: z.number().nullish(),
});
const ReturnTotalCalculationsSchema: z.ZodType<ReturnTotalCalculations> =
  ReturnTotalCalculationsSchema_;
export interface ReturnTotalCalculations
  extends z.infer<typeof ReturnTotalCalculationsSchema_> {}

const ReturnTotalCalculationsOutputSchema_ = z.object({
  instant: ReturnTotalCalculationsSchema,
  processed: ReturnTotalCalculationsSchema,
});
export const ReturnTotalCalculationsOutputSchema: z.ZodType<ReturnTotalCalculationsOutput> =
  ReturnTotalCalculationsOutputSchema_;
export interface ReturnTotalCalculationsOutput
  extends z.infer<typeof ReturnTotalCalculationsOutputSchema_> {}

const LineItemSchema_ = z.object({ id: z.string(), exchangeCount: z.number() });
const LineItemSchema: z.ZodType<LineItem> = LineItemSchema_;
export interface LineItem extends z.infer<typeof LineItemSchema_> {}

const OrderSchema_ = z.object({
  id: zExt.objectId(),
  warrantyId: zExt.objectId().nullish(),
  name: z.string(),
  tags: z.array(z.string()),
  orderDate: z.date(),
  totalPrice: z.string(),
  currency: z.string(),
  protected: z.boolean(),
  packageProtected: z.boolean(),
  finalSaleReturnsProtected: z.boolean(),
  productsCoveredByFinalSale: z.array(z.string()),
  paymentMethods: z.array(z.string()),
  salesChannel: z.string(),
  exchangeCount: z.number().nullish(),
  discounts: z.array(discountNameSchema),
  platform: z.nativeEnum(Provider),
  sourceNotes: z
    .union([z.string(), z.array(InputAnswerPartWithStepSchema)])
    .nullish(),
  coverage: z.object({
    exchange: z.boolean(),
    storeCredit: z.boolean(),
    refund: z.boolean(),
    repair: z.boolean(),
  }),
  lineItems: z.array(LineItemSchema).nullish(),
});
const OrderSchema: z.ZodType<Order> = OrderSchema_;
export interface Order extends z.infer<typeof OrderSchema_> {}

const NewOrderDataSchema_ = z.object({
  items: z.array(AdvancedExchangeItemSchema),
  /** @deprecated use totalPriceSet instead */
  totalPrice: z.string(), // without tax
  totalPriceSet: PriceSetSchema.nullish(),
  /** @deprecated use totalTaxSet instead */
  totalTax: z.string(),
  totalTaxSet: PriceSetSchema.nullish(),
  taxLineInfo: z
    .array(z.object({ title: z.string(), rate: z.number() }))
    .nullish(),
  discountCodes: z.array(z.string()),
  discountAllocations: z.array(
    z.object({
      /** @deprecated use discountedAmountSet instead */
      discountedAmount: z.object({
        amount: z.string(),
        currencyCode: z.string(),
      }),
      discountedAmountSet: PriceSetSchema.nullish(),
    }),
  ),
  blockAutomaticDiscounts: z.boolean().nullish(),
});
export const NewOrderDataSchema: z.ZodType<NewOrderData> = NewOrderDataSchema_;
export interface NewOrderData extends z.infer<typeof NewOrderDataSchema_> {}

// Main ReturnRequest schema
const DraftReturnSchema_ = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  treatmentHash: z.string(),
  flow: z.any(),
  finalizeFlow: z.any(),
  returnType: z.nativeEnum(ReturnTypeEnum),
  orders: z.array(OrderSchema), // I wish this was a Record<string, OrderSchema>. I wish mongoose supported this in a way that wasn't a map. plz lmk if you know how to do this
  customer: z.object({
    id: z.string().nullish(),
    shippingAddress: AddressSchema.nullish(),
    name: z.string(),
    tags: z.array(z.string()),
    numCustomerClaims: z.number(),
    numCustomerReturns: z.number(),
    numOrders: z.number(),
    // Add dates for time-based conditions
    orderDates: z.array(z.date()).nullish(),
    returnDates: z.array(z.date()).nullish(),
    returnRate: z.number(),
    newOrderAddress: AddressSchema.nullish(),
    timezone: z.string().nullish(),
    // This is an email that is set by the merchant during merchant created returns when the customer does not have an email on file.
    updatedEmail: z.string().nullish(),
    variantPreferences: FlattenedCustomerVariantPreferencesSchema.nullish(),
  }),
  featureFlags: z.record(z.string(), z.boolean()).nullish(),
  newOrderItems: z.array(NewExchangeItemSchema),
  newOrderData: NewOrderDataSchema.nullish(),
  returnableItems: z.array(ReturnableItemSchema),
  pendingReturnItems: z.array(PendingReturnItemSchema),
  compensationMethods: z.array(CompensationMethodSchema),
  thirdPartyRefundProvider: z.enum(ThirdPartyRefundProvider).nullish(),
  selectedMethod: z.nativeEnum(CompensationMethodType).nullish(),
  shipmentMethod: ShipmentMethodSchema.nullish(),
  shipments: z.array(DraftReturnShipmentSchema).nullish(),
  submitted: z.boolean().nullish(),
  rejected: z.boolean().nullish(),
  provisionType: z.nativeEnum(ProvisionType),
  currency: z.nativeEnum(Currency).nullish(),
  presentmentCurrency: z.nativeEnum(Currency).nullish(),
  pickupVariant: z.string().nullish(),
  referralId: z.string().nullish(),
  merchantCreated: z.boolean().nullish(),
  merchantName: z.string().nullish(),
  merchantRoles: z.array(z.string()).nullish(),
  repairFeeCollectionStrategy: z
    .nativeEnum(RepairFeeCollectionStrategy)
    .nullish(),
  tnplContractId: zExt.objectId().nullish(),
  upsellBillingTrigger: z.nativeEnum(UpsellBillingTrigger).nullish(),
  provider: z.nativeEnum(Provider).nullish(),
  channel: z.nativeEnum(Channel).nullish(),
  pos: z.object({ locationId: z.string() }).nullish(),
  exchangeRate: z.number().optional(),
  selectedExchangeShippingOption: z
    .object({
      rateTable: z.lazy(() => ShippingRateTableSchema),
      price: z.number(),
      currency: z.nativeEnum(Currency),
      name: z.string(), // Display name from the rate table
    })
    .nullish(),
  source: z.enum(DraftReturnSource).nullish(),
  newItemToExchangeRate: z.number().optional(),
  returnTags: z
    .array(z.object({ name: z.string(), color: z.string() }))
    .optional(),
  reviewingParty: z.nativeEnum(ReviewingParty).nullish(),
});
export const DraftReturnSchema: z.ZodType<DraftReturn> = DraftReturnSchema_;
export interface DraftReturn extends z.infer<typeof DraftReturnSchema_> {}

export type ReturnRequest = DraftReturn;

/**
 * If the draft return is going to have one or more items being exchanged.
 */
export function isDraftingExchangeReturn(draftReturn: DraftReturn): boolean {
  return (
    draftReturn.pendingReturnItems.some(
      (item) =>
        !!item.variantExchangeItem ||
        !!item.exchangeGroup ||
        (item.advancedExchangeItems?.length ?? 0) > 0,
    ) || (draftReturn.newOrderData?.items.length ?? 0) > 0
  );
}
